import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { UserSettings } from './UserSettings';
import { UserManagement } from '../Admin/UserManagement';
import { HolidayManagement } from '../Admin/HolidayManagement';
import { userAPI, adminAPI, monthlyOverviewAPI } from '../../services/api';
import { TimeTracking } from './TimeTracking';
import { VacationRequest } from './VacationRequest';
import './Dashboard.css';
import { BreakRulesManagement } from '../Admin/BreakRulesManagement';
import { WorkLogManagement } from '../Admin/WorkLogManagement';
import { TimeOffManagement } from '../Admin/TimeOffManagement';
import { AdminMonthlyOverview } from '../Admin/AdminMonthlyOverview';
import { MonthlyOverview } from './MonthlyOverview';
import { AdminDashboard } from '../Admin/AdminDashboard';
import { 
  BarChart3, 
  Clock,
  Calendar, 
  Settings, 
  Palmtree, 
  FileText, 
  TrendingUp, 
  Users, 
  Gift, 
  Timer, 
  Pause,
  Crown,
  LogOut,
  RefreshCw,
  Lock
} from 'lucide-react';
import { getRoleDisplayName } from '../../utils/roleTranslations';

interface DashboardProps {
  user: any;
}

interface TodayWorkSummary {
  workLogs: any[];
  totalMinutes: number;
  grossMinutes: number;
  totalBreakMinutes: number;
}

interface CurrentWorkStatus {
  isWorking: boolean;
  currentSession: {
    id: number;
    startTime: string;
    description: string | null;
    elapsedMinutes: number;
  } | null;
  elapsedMinutes: number;
}

interface CurrentOvertimeSummary {
  totalCumulativeOvertime: number;
  overtimeMinutesUpToToday: number;
  cumulativeOvertimeFromPrevious: number;
  userStartDate: string;
}

type ActiveView = 'overview' | 'timetracking' | 'vacation' | 'monthly' | 'settings' | 'admin-users' | 'admin-holidays' | 'admin-worklogs' | 'admin-break-rules' | 'admin-reports' | 'admin-dashboard' | 'admin-timeoff-management' | 'admin-monthly-overview';

export const Dashboard = ({ user }: DashboardProps) => {
  const { logout } = useAuth();
  const [activeView, setActiveView] = useState<ActiveView>('overview');
  const [todayWorkSummary, setTodayWorkSummary] = useState<TodayWorkSummary | null>(null);
  const [currentWorkStatus, setCurrentWorkStatus] = useState<CurrentWorkStatus | null>(null);
  const [currentOvertime, setCurrentOvertime] = useState<CurrentOvertimeSummary | null>(null);
  const [adminStats, setAdminStats] = useState({
    currentlyClockedIn: 0,
    activeUsers: 0,
    pendingActivation: 0
  });

  const [liveElapsedTime, setLiveElapsedTime] = useState('--:--');

  // Dark Mode permanent aktivieren
  useEffect(() => {
    document.body.classList.add('dark-mode');
    loadDashboardData();
    if (user.role === 'ADMIN') {
      loadAdminStats();
    }
  }, [user.role]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (currentWorkStatus?.isWorking && currentWorkStatus.currentSession) {
      interval = setInterval(() => {
        // Verbesserte Zeit-Berechnung mit UTC-Handling
        const startTime = new Date(currentWorkStatus.currentSession!.startTime);
        const now = new Date();
        
        // Sicherstellen, dass beide Zeiten korrekt sind
        if (isNaN(startTime.getTime())) {
          console.error('Invalid startTime:', currentWorkStatus.currentSession!.startTime);
          setLiveElapsedTime('--:--');
          return;
        }
        
        const diffMinutes = Math.floor((now.getTime() - startTime.getTime()) / (1000 * 60));
        
        // Negative Zeiten abfangen (passiert bei Zeitzonenunterschieden)
        if (diffMinutes < 0) {
          console.warn('Negative time difference detected, adjusting...');
          setLiveElapsedTime('00:00');
          return;
        }
        
        // Sicherstellen, dass alle Werte gültig sind
        const safeHours = Math.max(0, Math.floor(diffMinutes / 60));
        const safeMinutes = Math.max(0, diffMinutes % 60);
        
        setLiveElapsedTime(`${safeHours}:${safeMinutes.toString().padStart(2, '0')}`);
      }, 1000);
    } else {
      setLiveElapsedTime('--:--');
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentWorkStatus]);

  const loadDashboardData = async () => {
    await Promise.all([
      loadTodayWorkSummary(),
      loadCurrentWorkStatus(),
      loadCurrentOvertime()
    ]);
  };

  const loadTodayWorkSummary = async () => {
    try {
      const response = await userAPI.getTodayWorkStatus();
      setTodayWorkSummary(response.data);
    } catch (error) {
      console.error('Error loading today work summary:', error);
    }
  };

  const loadCurrentWorkStatus = async () => {
    try {
      const response = await userAPI.getCurrentWorkStatus();
      setCurrentWorkStatus(response.data);
    } catch (error) {
      console.error('Error loading current work status:', error);
    }
  };

  const loadCurrentOvertime = async () => {
    try {
      const now = new Date();
      const response = await monthlyOverviewAPI.getMonthlyOverview({
        year: now.getFullYear(),
        month: now.getMonth() + 1
      });
      
      setCurrentOvertime({
        totalCumulativeOvertime: response.data.summary.totalCumulativeOvertime,
        overtimeMinutesUpToToday: response.data.summary.overtimeMinutesUpToToday,
        cumulativeOvertimeFromPrevious: response.data.summary.cumulativeOvertimeFromPrevious,
        userStartDate: response.data.user.startDate
      });
    } catch (error) {
      console.error('Error loading current overtime:', error);
    }
  };

  const loadAdminStats = async () => {
    try {
      // Hole alle User-Daten
      const usersResponse = await adminAPI.getAllUsers({ limit: 1000 });
      const users = usersResponse.data.users;
      
      // Hole aktuell eingestempelte User mit adminAPI helper
      const dashboardResponse = await adminAPI.getDashboardStats();
      
      console.log('Dashboard stats response:', dashboardResponse.data); // Debug log
      
      setAdminStats({
        currentlyClockedIn: dashboardResponse.data.currentlyClockedIn || 0,
        activeUsers: users.filter((u: any) => u.isActive).length,
        pendingActivation: users.filter((u: any) => !u.isActive).length
      });
    } catch (error: any) {
      console.error('Error loading admin stats:', error);
      console.error('Error details:', error.response?.data); // More detailed error log
      // Set default values on error
      setAdminStats({
        currentlyClockedIn: 0,
        activeUsers: 0,
        pendingActivation: 0
      });
    }
  };

  const formatTodayTime = () => {
    if (!todayWorkSummary || todayWorkSummary.totalMinutes === 0) return '--:--';
    const hours = Math.floor(todayWorkSummary.totalMinutes / 60);
    const minutes = todayWorkSummary.totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const getWorkStatusText = () => {
    if (!currentWorkStatus) return 'Lädt...';
    return currentWorkStatus.isWorking ? 'Eingestempelt' : 'Ausgestempelt';
  };

  const getWorkStatusClass = () => {
    if (!currentWorkStatus) return '';
    return currentWorkStatus.isWorking ? 'working' : 'not-working';
  };

  const getWorkStatusDetail = () => {
    if (!currentWorkStatus) return 'Status wird geladen...';
    
    if (currentWorkStatus.isWorking && currentWorkStatus.currentSession) {
      const startTime = new Date(currentWorkStatus.currentSession.startTime);
      return `Seit ${startTime.toLocaleTimeString('de-DE')}`;
    } else if (todayWorkSummary && todayWorkSummary.workLogs.length > 0) {
      const lastLog = todayWorkSummary.workLogs[0];
      if (lastLog.endTime) {
        const endTime = new Date(lastLog.endTime);
        return `Letzte Aktivität: ${endTime.toLocaleTimeString('de-DE')}`;
      }
    }
    
    return 'Heute noch nicht eingestempelt';
  };

  // Menü-Items basierend auf Benutzerrolle
  const getMenuItems = () => {
    console.log('User object:', user); // Debug-Ausgabe
    console.log('User webTimeTrackingEnabled:', user?.webTimeTrackingEnabled); // Debug-Ausgabe

    const baseItems = [
      { id: 'overview', label: 'Übersicht', icon: BarChart3 },
      { id: 'vacation', label: 'Urlaub/Abwesenheit', icon: Palmtree },
      { id: 'monthly', label: 'Monatsübersicht', icon: Calendar },
      { id: 'settings', label: 'Benutzereinstellungen', icon: Settings }
    ];

    // Nur Zeiterfassung anzeigen, wenn die Web-Zeiterfassung aktiviert ist
    if (user?.webTimeTrackingEnabled === true) {
      baseItems.splice(1, 0, { id: 'timetracking', label: 'Zeiterfassung', icon: Clock });
    }

    // Admin/Manager/Accounting-spezifische Menü-Items
    if (['ADMIN', 'MANAGER'].includes(user?.role)) {
      baseItems.push(
        { id: 'admin-dashboard', label: 'Admin Dashboard', icon: BarChart3 }
      );
    }

    // Admin/Manager/Accounting-spezifische Menü-Items
    if (['ADMIN', 'MANAGER', 'ACCOUNTING'].includes(user?.role)) {
      baseItems.push(
        { id: 'admin-monthly-overview', label: 'Admin Monatsübersicht', icon: Calendar },
        { id: 'admin-timeoff-management', label: 'Urlaubs/Krankheits-Verwaltung', icon: FileText },
        { id: 'admin-reports', label: 'Admin Berichte', icon: TrendingUp }
      );
    }

    // Admin-spezifische Menü-Items hinzufügen
    if (user?.role === 'ADMIN') {
      baseItems.push(
        { id: 'admin-users', label: 'Benutzerverwaltung', icon: Users },
        { id: 'admin-holidays', label: 'Feiertage', icon: Gift },
        { id: 'admin-break-rules', label: 'Pausenregeln', icon: Pause },
        { id: 'admin-worklogs', label: 'Arbeitszeit-Verwaltung', icon: Timer }
      );
    }

    return baseItems;
  };

  // Neuer useEffect für das Neuladen bei Tab-Wechsel zur Übersicht
  useEffect(() => {
    if (activeView === 'overview') {
      console.log('🔄 Switched to overview, reloading data...');
      loadDashboardData();
      if (user.role === 'ADMIN') {
        loadAdminStats();
      }
    }
  }, [activeView]); // Wird ausgelöst wenn sich activeView ändert

  // Optional: Refresh-Button zur manuellen Aktualisierung
  const handleRefreshOverview = () => {
    loadDashboardData();
    if (user.role === 'ADMIN') {
      loadAdminStats();
    }
  };

  const formatMinutes = (minutes: number) => {
    if (minutes === 0) return '0:00';
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes < 0 ? '-' : '+';
    return `${sign}${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const renderContent = () => {
    switch (activeView) {
      case 'overview':
        return (
          <div className="content-section">
            <div className="overview-header">
              <h2>
                <BarChart3 size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Übersicht
              </h2>
              <button 
                className="refresh-btn" 
                onClick={handleRefreshOverview}
                title="Daten aktualisieren"
              >
                <RefreshCw size={16} style={{ marginRight: '4px' }} />
                Aktualisieren
              </button>
            </div>
            
            {/* Admin-Statistiken für Admins in der Übersicht */}
            {user.role === 'ADMIN' && (
              <div className="admin-overview-section">
                <h3>🔧 Administrator-Übersicht</h3>
                <div className="overview-cards admin-cards">
                  <div className="overview-card admin-card currently-working">
                    <h4>Aktuell eingestempelt</h4>
                    <p className="big-number">{adminStats.currentlyClockedIn}</p>
                    <small>Mitarbeiter im Dienst</small>
                  </div>
                  <div className="overview-card admin-card active">
                    <h4>Aktivierte Benutzer</h4>
                    <p className="big-number">{adminStats.activeUsers}</p>
                    <small>Gesamt aktive Accounts</small>
                  </div>
                  <div className="overview-card admin-card pending">
                    <h4>Wartende Aktivierung</h4>
                    <p className="big-number">{adminStats.pendingActivation}</p>
                    <small>Noch nicht freigeschaltet</small>
                  </div>
                </div>
              </div>
            )}

            {/* Standard Benutzer-Übersicht */}
            <div className="user-overview-section">
              <h3>
                <Users size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Persönliche Übersicht
              </h3>
              <div className="overview-cards">
                <div className="overview-card">
                  <h4>Heutige Arbeitszeit</h4>
                  <p className="big-number">
                    {currentWorkStatus?.isWorking ? liveElapsedTime : formatTodayTime()}
                  </p>
                  <small>
                    {currentWorkStatus?.isWorking ? 'Läuft gerade...' : 'Netto-Arbeitszeit'}
                    {todayWorkSummary && todayWorkSummary.totalBreakMinutes > 0 && !currentWorkStatus?.isWorking && (
                      ` (${Math.floor(todayWorkSummary.totalBreakMinutes / 60)}:${(todayWorkSummary.totalBreakMinutes % 60).toString().padStart(2, '0')} Pause)`
                    )}
                  </small>
                  <small>
                    (Der Timer startet mit der Serverzeit und kann bei Zeitabweichungen variieren.)
                  </small>
                </div>
                <div className="overview-card">
                  <h4>Status</h4>
                  <p className={`status-indicator ${getWorkStatusClass()}`}>
                    {getWorkStatusText()}
                  </p>
                  <small>{getWorkStatusDetail()}</small>
                </div>
                <div className={`overview-card ${currentOvertime && currentOvertime.totalCumulativeOvertime >= 0 ? 'positive' : 'negative'}`}>
                  <h4>Über-/Unterstunden bis heute</h4>
                  <p className="big-number">
                    {currentOvertime ? formatMinutes(currentOvertime.overtimeMinutesUpToToday) : '--:--'}
                  </p>
                  <small>
                    {currentOvertime ? (
                      <>
                        Kumuliert seit {new Date(currentOvertime.userStartDate).toLocaleDateString('de-DE')}
                      </>
                    ) : 'Lade...'}
                  </small>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'timetracking':
        // Zeiterfassung nur anzeigen, wenn `webTimeTrackingEnabled` aktiviert ist
        if (user?.webTimeTrackingEnabled === true) {
          return <TimeTracking user={user} />;
        } else {
          setActiveView('overview'); // Zurück zur Übersicht, wenn deaktiviert
          return null;
        }
      
      case 'vacation':
        return <VacationRequest user={user} />;
      
      case 'monthly':
        return <MonthlyOverview />;
      
      case 'settings':
        return <UserSettings user={user} />;

      case 'admin-users':
        return (
          <div className="content-section admin-content">
            <UserManagement onStatsUpdate={loadAdminStats} />
          </div>
        );

      case 'admin-holidays':
        return (
          <div className="content-section admin-content">
            <HolidayManagement />
          </div>
        );

      case 'admin-dashboard':
        return (
          <div className="content-section admin-content">
            <AdminDashboard />
          </div>
        );

      case 'admin-reports':
        return (
          <div className="content-section admin-content">
            <h2>
              <TrendingUp size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Administrator Berichte
            </h2>
            <div className="admin-reports-section">
              <p>Erweiterte Berichts-Funktionen für Administratoren werden hier implementiert...</p>
              
              <div className="reports-placeholder">
                <div className="report-card">
                  <h3>
                    <BarChart3 size={20} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Arbeitszeit-Berichte
                  </h3>
                  <p>Übersicht über Arbeitszeiten aller Mitarbeiter</p>
                  <button className="btn secondary" disabled>
                    Bald verfügbar
                  </button>
                </div>
                
                <div className="report-card">
                  <h3>
                    <Palmtree size={20} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Urlaubs-Berichte
                  </h3>
                  <p>Übersicht über Urlaubsanträge und -genehmigungen</p>
                  <button className="btn secondary" disabled>
                    Bald verfügbar
                  </button>
                </div>
                
                <div className="report-card">
                  <h3>
                    <Lock size={20} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> 
                    Sicherheits-Berichte
                  </h3>
                  <p>Login-Aktivitäten und Sicherheitsanalysen</p>
                  <button className="btn secondary" disabled>
                    Bald verfügbar
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'admin-break-rules':
        return (
          <div className="content-section admin-content">
            <BreakRulesManagement />
          </div>
        );

      case 'admin-worklogs':
        return (
          <div className="content-section admin-content">
            <WorkLogManagement />
          </div>
        );

      case 'admin-timeoff-management':
        return (
          <div className="content-section admin-content">
            <TimeOffManagement user={user} />
          </div>
        );

      case 'admin-monthly-overview':
        return (
          <div className="content-section admin-content">
            <AdminMonthlyOverview />
          </div>
        );
      
      default:
        return <div>Unbekannte Ansicht</div>;
    }
  };

  const menuItems = getMenuItems();

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1><img src="/chronios.svg" alt="Chronios" style={{ width: '170px', height: 'auto' }} /></h1>
          <span className="user-greeting">
            Willkommen, {user.firstName} {user.lastName}!
            {user.role === 'ADMIN' && (
              <span className="admin-badge">
                <Crown size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Administrator
              </span>
            )}
          </span>
        </div>
        <div className="header-right">
          <span className="user-info">{user.email} ({getRoleDisplayName(user.role)})</span>
          <button className="logout-btn" onClick={logout}>
            <LogOut size={16} style={{ marginRight: '4px' }} />
            Abmelden
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <nav className="sidebar">
          <div className="menu-items">
            {menuItems.map((item) => (
              <button
                key={item.id}
                className={`menu-item ${activeView === item.id ? 'active' : ''} ${
                  item.id.startsWith('admin-') ? 'admin-menu-item' : ''
                }`}
                onClick={() => setActiveView(item.id as ActiveView)}
              >
                <span className="menu-icon">
                  <item.icon size={20} />
                </span>
                <span className="menu-label">{item.label}</span>
                {item.id.startsWith('admin-') && (
                  <span className="admin-indicator">
                    <Crown size={16} />
                  </span>
                )}
              </button>
            ))}
          </div>
        </nav>

        <main className="main-content">
          {renderContent()}
        </main>
      </div>

    </div>
  );
};